// src/domain/suppliers.js
// Reglas de la asignación de proveedores a eventos, sin DOM ni Supabase
// (tests/suppliers.domain.test.js).
//
// Cada evento tiene como mucho una línea por servicio en event_suppliers
// (migración 20261029_supplier_access.sql). "Tarima y luces" del mismo
// proveedor son dos líneas. El proveedor solo ve el bolo en su perfil cuando
// su línea está confirmada y el evento está confirmado o realizado; esa regla
// vive en la función supplier_events() y aquí se repite solo para la interfaz.

import { STAGES, SUPPLIER_TYPES } from "./constants.js";

// Orden en el que se muestran en la ficha del evento.
export const EVENT_SUPPLIER_SERVICES = Object.freeze([
  SUPPLIER_TYPES.STAGE,
  SUPPLIER_TYPES.LIGHTING,
  SUPPLIER_TYPES.SOUND,
  SUPPLIER_TYPES.TRANSPORT,
]);

const SERVICE_LABELS = Object.freeze({
  [SUPPLIER_TYPES.STAGE]: "Tarima",
  [SUPPLIER_TYPES.LIGHTING]: "Luces",
  [SUPPLIER_TYPES.SOUND]: "Sonido",
  [SUPPLIER_TYPES.TRANSPORT]: "Transporte",
});

export const SUPPLIER_LINE_STATUS = Object.freeze({
  PROPOSED: "proposed",
  CONFIRMED: "confirmed",
});

const STATUSES = Object.values(SUPPLIER_LINE_STATUS);

// Se elige proveedor desde que se envía el presupuesto (José, 29/10/2026).
const ASSIGN_STAGES = Object.freeze([
  STAGES.PROPOSAL,
  STAGES.NEGOTIATION,
  STAGES.ACCEPTED_PENDING_DEPOSIT,
  STAGES.CONFIRMED,
  STAGES.DONE,
]);

const VISIBLE_STAGES = Object.freeze([STAGES.CONFIRMED, STAGES.DONE]);

export function supplierServiceLabel(service) {
  return SERVICE_LABELS[service] || service;
}

export function canAssignSuppliers(stage) {
  return ASSIGN_STAGES.includes(stage);
}

export function isVisibleToSupplier(eventStage, lineStatus) {
  return VISIBLE_STAGES.includes(eventStage) && lineStatus === SUPPLIER_LINE_STATUS.CONFIRMED;
}

function contactTypes(contact) {
  const list = Array.isArray(contact?.supplier_types) && contact.supplier_types.length
    ? contact.supplier_types
    : [contact?.supplier_type];
  // "van" es el valor antiguo de transporte.
  return list.filter(Boolean).map(t => (t === SUPPLIER_TYPES.VAN ? SUPPLIER_TYPES.TRANSPORT : t));
}

export function supplierDisplayName(contact) {
  const person = [contact?.name, contact?.surname].filter(Boolean).join(" ").trim();
  return String(contact?.company || "").trim() || person || "Proveedor sin nombre";
}

// Proveedores que hacen ese servicio, por nombre. Si la línea ya tiene un
// proveedor que ha dejado de tener ese tipo, se mantiene en la lista para no
// perder la asignación al abrir la ficha.
export function supplierCandidates(contacts, service, currentContactId = "") {
  return (contacts || [])
    .filter(c => c?.is_supplier && (contactTypes(c).includes(service) || (currentContactId && c.id === currentContactId)))
    .sort((a, b) => supplierDisplayName(a).localeCompare(supplierDisplayName(b), "es", { sensitivity: "base" }));
}

function cleanNotes(value) {
  const s = String(value ?? "").trim();
  return s || null;
}

// Compara las líneas leídas al abrir la ficha con lo elegido en ella.
// desired: { [service]: { contact_id, status?, notes_for_supplier? } | null }.
// - Un servicio sin proveedor (null o contact_id vacío) se borra.
// - Un servicio que no aparece en desired no se toca.
// - Sin status: se mantiene el de la línea, salvo si cambia el proveedor, que
//   vuelve a "propuesto" (el nuevo aún no ha confirmado).
// - Sin notes_for_supplier: se mantiene la nota de la línea.
// Las actualizaciones y borrados llevan el updated_at leído, para no pisar
// cambios de otra persona (ver eventSuppliersService.js).
export function planSupplierChanges(currentRows, desired) {
  const inserts = [];
  const updates = [];
  const deletes = [];
  const byService = new Map((currentRows || []).map(r => [r.service, r]));

  for (const [service, choice] of Object.entries(desired || {})) {
    if (!EVENT_SUPPLIER_SERVICES.includes(service)) {
      throw new Error(`Servicio de proveedor desconocido: ${service}`);
    }
    const current = byService.get(service);
    const contactId = String(choice?.contact_id || "").trim();

    if (!contactId) {
      if (current) deletes.push({ id: current.id, updated_at: current.updated_at });
      continue;
    }

    const sameContact = current?.contact_id === contactId;
    const status = choice.status ?? (sameContact ? current.status : SUPPLIER_LINE_STATUS.PROPOSED);
    if (!STATUSES.includes(status)) {
      throw new Error(`Estado de proveedor desconocido: ${status}`);
    }
    const notes = choice.notes_for_supplier === undefined
      ? cleanNotes(current?.notes_for_supplier)
      : cleanNotes(choice.notes_for_supplier);
    const line = { contact_id: contactId, status, notes_for_supplier: notes };

    if (!current) {
      inserts.push({ service, ...line });
    } else if (
      !sameContact
      || current.status !== line.status
      || cleanNotes(current.notes_for_supplier) !== line.notes_for_supplier
    ) {
      updates.push({ id: current.id, updated_at: current.updated_at, changes: line });
    }
  }

  return { inserts, updates, deletes };
}

// Ordena las líneas como se muestran en la ficha.
export function sortSupplierLines(rows) {
  const pos = s => {
    const i = EVENT_SUPPLIER_SERVICES.indexOf(s);
    return i === -1 ? EVENT_SUPPLIER_SERVICES.length : i;
  };
  return [...(rows || [])].sort((a, b) => pos(a.service) - pos(b.service));
}

// Técnica del evento: tarima, luces y sonido salen de event_suppliers y
// sustituyen a las antiguas tareas "Tarima definida", "Luces definidas" y
// "Sonido definido" (tarea 4b). El transporte sigue en la tarea "Furgoneta",
// que admite "No hace falta".
export const TECHNICAL_SUPPLIER_SERVICES = Object.freeze([
  SUPPLIER_TYPES.STAGE,
  SUPPLIER_TYPES.LIGHTING,
  SUPPLIER_TYPES.SOUND,
]);

// Una fila por servicio técnico. El nombre sale de supplier_name si la línea
// ya lo trae (crew_event_suppliers) o del contacto; si el contacto no está
// cargado, se dice que hay proveedor sin inventar el nombre.
export function technicalSupplierRows(lines, contactsById = new Map()) {
  const byService = new Map((lines || []).map(l => [l.service, l]));
  return TECHNICAL_SUPPLIER_SERVICES.map(service => {
    const line = byService.get(service);
    const contact = line ? contactsById?.get(line.contact_id) : null;
    const name = !line ? "" : (String(line.supplier_name || "").trim()
      || (contact ? supplierDisplayName(contact) : "Proveedor asignado"));
    return {
      service,
      label: supplierServiceLabel(service),
      name,
      status: line?.status || "",
      confirmed: line?.status === SUPPLIER_LINE_STATUS.CONFIRMED,
      notes: String(line?.notes_for_supplier || "").trim(),
    };
  });
}

// Para el progreso de producción: cada servicio técnico cuenta como hecho
// cuando su proveedor está confirmado.
export function technicalSupplierStats(lines) {
  const total = TECHNICAL_SUPPLIER_SERVICES.length;
  const completed = technicalSupplierRows(lines).filter(r => r.confirmed).length;
  return { total, completed, pending: total - completed };
}

// "Mis bolos" del proveedor: próximos (hoy incluido, del más cercano al más
// lejano) y anteriores (del más reciente). today: "aaaa-mm-dd".
export function splitSupplierEvents(rows, today) {
  const list = [...(rows || [])];
  const upcoming = list.filter(r => r.event_date >= today).sort((a, b) => a.event_date.localeCompare(b.event_date));
  const past = list.filter(r => r.event_date < today).sort((a, b) => b.event_date.localeCompare(a.event_date));
  return { upcoming, past };
}

// Enlace a Google Maps con el lugar del bolo, o "" si no hay datos.
export function supplierMapsUrl(row) {
  const query = [row?.venue_name, row?.venue_address, row?.venue_city]
    .map(v => String(v ?? "").trim())
    .filter(Boolean)
    .join(", ");
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : "";
}
